import React from 'react';
import {
  SpaceDashboardOutlined as DashboardIcon,
  People as PeopleIcon,
  PeopleAltOutlined as PersonIcon,
  Inventory2Outlined as InventoryIcon,
  AccountBalanceWalletOutlined as DebtIcon,
  ReceiptLongOutlined as ExpenseIcon,
  PointOfSaleOutlined as SalesIcon,
} from '@mui/icons-material';
import { Language } from '../contexts/LanguageContext';

export const getHelpContent = (language: Language) => {
  if (language === 'en') {
    return {
      title: 'Help',
      subtitle: 'Find guides and answers for using Veresiye.',
      stepByStep: 'Step by Step:',
      features: [
        {
          id: 'dashboard',
          title: 'Dashboard',
          icon: <DashboardIcon />,
          description: 'Record summaries and shortcuts for common tasks',
          steps: [
            'Open the Dashboard to review customer and item counts; additional summaries depend on your role',
            'Use the Quick Actions cards to start tasks available to your role',
            'Select the settings icon beside Quick Actions to choose which actions to show and drag to reorder them',
          ],
        },
        {
          id: 'users',
          title: 'Users',
          icon: <PeopleIcon />,
          description: 'Learn how employee accounts are created and managed',
          steps: [
            'Company Administrators can open Users and choose Add Employee to create an employee account',
            'Enter first name, last name, username, and a password; email is optional',
            'Company Administrators can use the row actions (⋮) menu to edit or delete employee accounts they are allowed to manage. Contact your Company Administrator if you need an account',
          ],
        },
        {
          id: 'customers',
          title: 'Customer Management',
          icon: <PersonIcon />,
          description: 'Find customers and review their account details',
          steps: [
            'Open Customers and search by the customer’s first or last name',
            'Select a customer’s name to open their details and account history',
            'Company Administrators can choose Add Customer; first and last name are required, while phone, email, address, and tax identification number are optional',
            'Company Administrators can use Bulk Upload to download the CSV template and import customer rows; firstName and lastName columns are required',
            'If your account has permission, use the row actions (⋮) menu to edit or delete a customer; deletion is blocked when the customer has sales or debt records',
          ],
        },
        {
          id: 'items',
          title: 'Items',
          icon: <InventoryIcon />,
          description: 'Search the catalog and view item inventory',
          steps: [
            'Open Items and search by item name',
            'Use Filters to narrow the list by price or category',
            'Open an item’s inventory view to see its stock, batches, and transaction history',
            'Company Administrators can choose Add Item and select Product or Service; products require a price and starting stock quantity, while services do not track stock',
            'Company Administrators can use Bulk Upload with the CSV template; imported items start with zero stock, so add stock separately',
            'Company Administrators can use Manage Categories to create categories, then assign one or more categories while adding or editing an item',
            'If your account has permission, use the row actions (⋮) menu to edit or delete catalog items',
          ],
        },
        {
          id: 'inventory',
          title: 'Inventory',
          icon: <InventoryIcon />,
          description: 'Review stock, batches, and inventory history',
          steps: [
            'Open an item’s inventory view from the Items list',
            'Review the stock summary, batch list when batch tracking is enabled, and transaction history',
            'If these controls are available to your account, use Add Stock, Remove Stock, or Adjust Stock to change quantities',
            'When creating a new tracked batch, enter its required batch number; production and expiration dates are optional',
            'Company Administrators manage the company-wide batch tracking setting from Profile. Availability of individual item settings depends on your account permissions',
            'The item price is the default for new batches; changing it does not update existing batch prices',
          ],
        },
        {
          id: 'sales',
          title: 'Sales',
          icon: <SalesIcon />,
          description: 'Build a cart, complete a sale, and review sales history',
          steps: [
            'After signing in with an account that has access to Sales, open the page and select a customer',
            'Search for products or services, add them to the cart, and review quantities',
            'Open the cart and choose Full Payment, Partial Payment, or No Payment',
            'For a partial payment, enter the amount received, then choose Complete Sale',
            'Open Sales History to review completed sales and their payment status',
          ],
        },
        {
          id: 'expenses',
          title: 'Expenses',
          icon: <ExpenseIcon />,
          description: 'Record and review company expenses',
          steps: [
            'The Expenses page is available to Company Administrators. Open it and choose Add Expense',
            'Enter a description, amount, and date; category, payment method, paid status, and notes are also available',
            'Use the search and filters to find expenses by text, category, paid status, or date range',
          ],
        },
        {
          id: 'debts',
          title: 'Customer Accounts: Debts and Payments',
          icon: <DebtIcon />,
          description: 'Record customer debts and payments from the Customer Accounts page',
          steps: [
            'After signing in to an account with access to Customer Accounts, open the page and choose Add Debt or Add Payment',
            'Select a customer and enter the amount; the date and time default to now and can be changed',
            'Add a description if it helps identify the movement',
            'Use Filters to set a date or amount range; available filter options depend on your account permissions',
            'Select a customer’s name to review their account history',
          ],
        },
      ],
    };
  }

  return {
    title: 'Yardım',
    subtitle: 'Veresiye’yi kullanmaya yönelik kılavuzları ve yanıtları bulun.',
    stepByStep: 'Adım Adım:',
    features: [
      {
        id: 'dashboard',
        title: 'Ana Sayfa',
        icon: <DashboardIcon />,
        description: 'Kayıt özetleri ve sık kullanılan işlemlere kısayollar',
        steps: [
          'Ana sayfada müşteri ve ürün sayılarını inceleyin; ek özetler rolünüze göre gösterilir',
          'Rolünüze açık işlemleri başlatmak için Hızlı İşlemler kartlarını kullanın',
          'Göstermek istediğiniz işlemleri seçmek ve sıralamak için Hızlı İşlemler başlığının yanındaki ayarlar simgesini kullanın',
        ],
      },
      {
        id: 'users',
        title: 'Kullanıcılar',
        icon: <PeopleIcon />,
        description: 'Çalışan hesaplarının nasıl oluşturulduğunu ve yönetildiğini öğrenin',
        steps: [
          'Şirket Yöneticileri Kullanıcılar sayfasını açıp Çalışan Ekle’yi seçerek çalışan hesabı oluşturabilir',
          'Ad, soyad, kullanıcı adı ve şifre girin; e-posta isteğe bağlıdır',
          'Şirket Yöneticileri, yönetme izinleri olan çalışan hesaplarını satırdaki işlemler (⋮) menüsünden düzenleyebilir veya silebilir. Hesaba ihtiyacınız varsa Şirket Yöneticinizle iletişime geçin',
        ],
      },
      {
        id: 'customers',
        title: 'Müşteri Yönetimi',
        icon: <PersonIcon />,
        description: 'Müşterileri bulma ve hesap detaylarını inceleme',
        steps: [
          'Müşteriler sayfasını açıp müşteriyi ad veya soyadına göre arayın',
          'Müşteri adına tıklayarak detaylarını ve hesap geçmişini açın',
          'Şirket Yöneticileri Müşteri Ekle’yi seçebilir; ad ve soyad zorunlu, telefon, e-posta, adres ve vergi kimlik numarası isteğe bağlıdır',
          'Şirket Yöneticileri Toplu Yükleme ile CSV şablonunu indirip müşteri satırlarını aktarabilir; firstName ve lastName sütunları zorunludur',
          'Hesabınızda izin varsa satırdaki işlemler (⋮) menüsünden müşteriyi düzenleyin veya silin; satış ya da borç kaydı olan müşteri silinemez',
        ],
      },
      {
        id: 'items',
        title: 'Ürünler',
        icon: <InventoryIcon />,
        description: 'Ürün kataloğunda arama ve envanter bilgilerini görüntüleme',
        steps: [
          'Ürünler sayfasını açıp ürün adına göre arama yapın',
          'Listeyi fiyat veya kategoriye göre daraltmak için Filtreler’i kullanın',
          'Stok miktarını, partileri ve işlem geçmişini görmek için ürünün envanter görünümünü açın',
          'Şirket Yöneticileri Ürün Ekle’yi seçip Ürün veya Hizmet türünü belirleyebilir; ürünlerde fiyat ve başlangıç stoğu zorunludur, hizmetlerde stok takibi yapılmaz',
          'Şirket Yöneticileri CSV şablonuyla Toplu Yükleme yapabilir; yüklenen ürünlerin stoğu sıfır başlar, bu nedenle stoğu ayrıca ekleyin',
          'Şirket Yöneticileri Kategorileri Yönet kontrolüyle kategori oluşturabilir; ürün eklerken veya düzenlerken bir ya da daha fazla kategori atayabilir',
          'Hesabınızda izin varsa katalog öğelerini düzenlemek veya silmek için satırdaki işlemler (⋮) menüsünü kullanın',
        ],
      },
      {
        id: 'inventory',
        title: 'Envanter',
        icon: <InventoryIcon />,
        description: 'Stok, partiler ve envanter geçmişini inceleme',
        steps: [
          'Ürünler listesinden bir ürünün envanter görünümünü açın',
          'Stok özetini, parti takibi açıksa parti listesini ve işlem geçmişini inceleyin',
          'Hesabınızda bu kontroller kullanılabiliyorsa miktarı değiştirmek için Stok Ekle, Stok Çıkar veya Stok Ayarla işlemlerini kullanın',
          'Takipli yeni parti oluştururken zorunlu parti numarasını girin; üretim ve son kullanma tarihleri isteğe bağlıdır',
          'Şirket genelindeki parti takibi ayarını Şirket Yöneticileri Profil sayfasından yönetir. Tekil ürün ayarlarının kullanılabilirliği hesap izinlerinize bağlıdır',
          'Ürün fiyatı yeni partiler için varsayılandır; bu fiyatı değiştirmek mevcut parti fiyatlarını güncellemez',
        ],
      },
      {
        id: 'sales',
        title: 'Satışlar',
        icon: <SalesIcon />,
        description: 'Sepet oluşturma, satış tamamlama ve satış geçmişini inceleme',
        steps: [
          'Giriş yaptıktan sonra Satışlar sayfasına erişimi olan bir hesapla sayfayı açıp müşteriyi seçin',
          'Ürün veya hizmet arayıp sepete ekleyin ve miktarları gözden geçirin',
          'Sepeti açıp Tam Ödeme, Kısmi Ödeme veya Ödeme Yok seçeneklerinden birini belirleyin',
          'Kısmi ödeme için alınan tutarı girip Satışı Tamamla’yı seçin',
          'Tamamlanan satışları ve ödeme durumlarını incelemek için Satış Geçmişi’ni açın',
        ],
      },
      {
        id: 'expenses',
        title: 'Giderler',
        icon: <ExpenseIcon />,
        description: 'Şirket giderlerini kaydetme ve inceleme',
        steps: [
          'Giderler sayfası Şirket Yöneticilerine açıktır. Sayfayı açıp Gider Ekle’yi seçin',
          'Açıklama, tutar ve tarih girin; kategori, ödeme yöntemi, ödendi durumu ve notlar da eklenebilir',
          'Giderleri metin, kategori, ödeme durumu veya tarih aralığına göre bulmak için arama ve filtreleri kullanın',
        ],
      },
      {
        id: 'debts',
        title: 'Cari Hesaplar: Borç ve Ödemeler',
        icon: <DebtIcon />,
        description: 'Cari Hesaplar sayfasından müşteri borçlarını ve ödemelerini kaydetme',
        steps: [
          'Giriş yaptıktan sonra Cari Hesaplar sayfasına erişimi olan bir hesapla sayfayı açıp Borç ekle veya Ödeme Ekle’yi seçin',
          'Müşteriyi belirleyip tutarı girin; tarih ve saat varsayılan olarak şu anı gösterir, isterseniz değiştirebilirsiniz',
          'Hareketi tanımlamaya yardımcı olacak bir açıklama ekleyebilirsiniz',
          'Tarih veya tutar aralığı belirlemek için Filtreler’i kullanın; kullanılabilir filtreler hesap izinlerinize bağlıdır',
          'Müşteri hesap geçmişini incelemek için adına tıklayın',
        ],
      },
    ],
  };
};

export const getQuickStartContent = (language: Language) => language === 'en'
  ? {
    title: 'Start here',
    description: 'Follow these steps to set up Veresiye and complete your first transaction.',
    steps: [
      { id: 'profile', title: 'Complete your profile', description: 'Add your company details and review settings.', href: '/help/users' },
      { id: 'customers', title: 'Add your first customer', description: 'Create the customer record you will use in a sale or account movement.', href: '/help/customers' },
      { id: 'items', title: 'Add a product or service', description: 'Create the item and choose whether it needs stock tracking.', href: '/help/items' },
      { id: 'inventory', title: 'Add stock when needed', description: 'Add opening stock before selling a tracked product.', href: '/help/inventory' },
      { id: 'sales', title: 'Record the first sale', description: 'Choose the customer, fill the cart, and select the payment status.', href: '/help/sales' },
    ],
  }
  : {
    title: 'Buradan başlayın',
    description: 'Veresiye’yi kurmak ve ilk işleminizi tamamlamak için şu sırayı izleyin.',
    steps: [
      { id: 'profile', title: 'Profilinizi tamamlayın', description: 'Şirket bilgilerinizi ekleyin ve ayarları gözden geçirin.', href: '/help/users' },
      { id: 'customers', title: 'İlk müşterinizi ekleyin', description: 'Satışta veya cari hesap hareketinde kullanacağınız müşteri kaydını oluşturun.', href: '/help/customers' },
      { id: 'items', title: 'Ürün veya hizmet ekleyin', description: 'Kaydı oluşturun ve stok takibi gerekip gerekmediğini belirleyin.', href: '/help/items' },
      { id: 'inventory', title: 'Gerekiyorsa stok ekleyin', description: 'Stok takipli bir ürünü satmadan önce başlangıç stoğunu ekleyin.', href: '/help/inventory' },
      { id: 'sales', title: 'İlk satışınızı kaydedin', description: 'Müşteriyi seçin, sepeti doldurun ve ödeme durumunu belirleyin.', href: '/help/sales' },
    ],
  };

export const getCurrentCapabilities = (language: Language) => language === 'en'
  ? {
    title: 'What you can do in Veresiye today',
    description: 'Veresiye brings customer accounts, products, sales, stock, and company operations together in one place.',
    items: [
      { title: 'Customers and accounts', description: 'Keep customer details, review account history, record debts and payments, and filter movements.' },
      { title: 'Products and services', description: 'Manage a catalog, prices, VAT, categories, barcodes, and service items that do not require stock.' },
      { title: 'Stock and batches', description: 'Add, remove, or adjust stock; track batches with batch numbers, dates, and separate prices when enabled.' },
      { title: 'Sales and payments', description: 'Build a cart, sell products or services, and record full, partial, or no payment.' },
      { title: 'Company management', description: 'Manage employees, roles, company expenses, profile settings, and email/password actions according to your permissions.' },
      { title: 'Bulk work and mobile use', description: 'Import customers or items with CSV templates and use the responsive interface from a phone browser.' },
    ],
  }
  : {
    title: 'Veresiye ile bugün neler yapabilirsiniz?',
    description: 'Veresiye; müşteri hesaplarını, ürünleri, satışları, stokları ve şirket işlemlerini tek yerde birleştirir.',
    items: [
      { title: 'Müşteriler ve cari hesaplar', description: 'Müşteri bilgilerini tutun, hesap geçmişini inceleyin, borç ve ödeme kaydedin, hareketleri filtreleyin.' },
      { title: 'Ürünler ve hizmetler', description: 'Katalog, fiyat, KDV, kategori, barkod ve stok gerektirmeyen hizmet kayıtlarını yönetin.' },
      { title: 'Stok ve parti takibi', description: 'Stok ekleyin, çıkarın veya düzeltin; özellik açıksa parti numarası, tarihler ve ayrı fiyatlarla takip edin.' },
      { title: 'Satış ve ödeme', description: 'Sepet oluşturun, ürün veya hizmet satın ve tam, kısmi ya da ödemesiz satış kaydedin.' },
      { title: 'Şirket yönetimi', description: 'Yetkinize göre çalışanları, rolleri, şirket giderlerini, profil ayarlarını ve şifre işlemlerini yönetin.' },
      { title: 'Toplu işlem ve mobil kullanım', description: 'Müşteri veya ürünleri CSV şablonlarıyla toplu aktarın ve uygulamayı telefon tarayıcısından kullanın.' },
    ],
  };
